/**
 * Google Maps react component that uses the [google-map-react](https://github.com/google-map-react/google-map-react) library
 *
 * Docs: https://developers.google.com/maps/documentation/javascript/examples/
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import GoogleMapReact from 'google-map-react'
import MapMarker from './components/MapMarker'
import CurrentLocationButton from './components/CurrentLocationButton'
import MapControl from './components/MapControl'
import { bootstrapURLKeys } from './components/GoogleMapsApiWrapper'
import { trackEvent } from 'client/shared/js/utils/tracking'
import styles from './GoogleMaps.module.scss'

const geolocationErrorCodes = {
  'PERMISSION_DENIED': 1,
  'POSITION_UNAVAILABLE': 2,
  'TIMEOUT': 3
}

const createMapOptions = (maps, options = {}) => {
  return {
    clickableIcons: false,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_BOTTOM
    },
    fullscreenControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT
    },
    ...options
  }
}

class GoogleMaps extends Component {
  static propTypes = {
    height: PropTypes.string,
    width: PropTypes.string,
    mapConfig: PropTypes.object,
    onMapLoad: PropTypes.func,
    onPermissionDenied: PropTypes.func,
    onDrag: PropTypes.func,
    onZoomAnimationStart: PropTypes.func,
    enableGeolocation: PropTypes.bool,
    controls: PropTypes.array,
    options: PropTypes.object,
    className: PropTypes.string,
    children: PropTypes.node
  }

  static defaultProps = {
    className: '',
    height: '100%',
    width: '100%',
    enableGeolocation: false,
    controls: [],
    options: {},
    onMapLoad: () => false,
    onPermissionDenied: () => false,
    onDrag: () => false,
    onZoomAnimationStart: () => false,
    mapConfig: {}
  }

  _isMounted = false

  state = {
    map: null,
    maps: null,
    isGeolocationAvailable: false,
    coords: {}
  }

  componentDidMount () {
    if (this.props.enableGeolocation) {
      this._isMounted = true
      this.getCurrentPosition()
    }
  }

  componentWillUnmount () {
    this._isMounted = false
  }

  onCurrentLocationBtnClick = () => {
    if (this.state.map && this.state.isGeolocationAvailable) {
      const { latitude, longitude } = this.state.coords
      const position = new this.state.maps.LatLng(latitude, longitude)
      const bounds = this.state.map.getBounds()

      if (!bounds.contains(position)) {
        bounds.extend(position)
        this.state.map.fitBounds(bounds)
      }
    } else {
      navigator.permissions.query({
        'name': 'geolocation'
      }).then((permission) => {
        if (permission && (permission.state === 'denied' || permission.state === 'prompt')) {
          this.props.onPermissionDenied('To enable this feature, please enable sharing of your current location on this browser.')
        }
      })
    }
  }

  getCurrentPosition = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(this.handleGeolocationSuccess, this.handleGeolocationError, {
        enableHighAccuracy: true,
        timeout: 150000 // 15 seconds
      })
    }
  }

  handleGeolocationError = (err) => {
    switch (err.code) {
      case [geolocationErrorCodes.TIMEOUT]:
        trackEvent('Get user location error - timeout')
        break
      case [geolocationErrorCodes.POSITION_UNAVAILABLE]:
        trackEvent('Get user location error - data could not be retrieved')
        break
      case [geolocationErrorCodes.PERMISSION_DENIED]:
        trackEvent('Get user location error - user declined')
        break
      default:
    }
  }

  handleGeolocationSuccess = (position) => {
    // Prevents memory leak when loading the map doesn't complete and the user navigates away
    if (this._isMounted) {
      this.setState({
        isGeolocationAvailable: true,
        coords: position.coords
      })
    }
  }

  onGoogleApiLoaded = ({ map, maps }) => {
    this.props.onMapLoad({ map, maps })

    this.setState({ map, maps })
  }

  renderCurrentLocation = () => {
    if (this.state.isGeolocationAvailable) {
      const { latitude, longitude } = this.state.coords

      return (
        <MapMarker
          id={`current-location-marker`}
          color='#4285EA'
          height='20px'
          width='20px'
          lat={latitude}
          lng={longitude}
          pulsate />
      )
    }

    return null
  }

  renderCurrentLocationButton = () => {
    return this.props.enableGeolocation ? (
      <MapControl
        map={this.state.map}
        maps={this.state.maps}
        position='RIGHT_BOTTOM'>
        <div className={cx(styles['google-map-btn'])}>
          <CurrentLocationButton onClick={this.onCurrentLocationBtnClick} />
        </div>
      </MapControl>
    ) : null
  }

  renderMapControls = () => {
    return this.props.controls.map(({ key, component, position }, index) => {
      return (
        <MapControl
          key={key ?? `map-control-${index}`}
          map={this.state.map}
          maps={this.state.maps}
          position={position}>
          <div className={cx(styles['google-map-btn'])}>
            {component}
          </div>
        </MapControl>
      )
    })
  }

  render () {
    const mapConfig = {
      zoom: 10,
      hoverDistance: 30,
      ...this.props.mapConfig
    }

    const mapProps = {
      bootstrapURLKeys,
      geometryLibrary: true,
      placesLibrary: true,
      yesIWantToUseGoogleMapApiInternals: true,
      onGoogleApiLoaded: this.onGoogleApiLoaded,
      onZoomAnimationStart: this.props.onZoomAnimationStart,
      onDrag: this.props.onDrag,
      options: (maps) => createMapOptions(maps, this.props.options),
      ...mapConfig
    }

    const mapStyle = {
      height: this.props.height,
      width: this.props.width
    }

    return (
      <div className={this.props.className}>
        <div style={mapStyle} className='map-container'>
          <GoogleMapReact {...mapProps}>
            {this.props.children}
            {this.renderCurrentLocation()}
            {this.renderCurrentLocationButton()}
            {this.renderMapControls()}
          </GoogleMapReact>
        </div>
      </div>
    )
  }
}

export default GoogleMaps
